import {useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {format} from 'date-fns/format'
import {parseISO} from 'date-fns/parseISO'
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import {
  getAnalyticsPageName,
  getAnonymousId,
  getLanguageCode,
  getUserCountryCode
} from 'modules/meta/selectors'
import {getSearchParameters} from 'modules/sapiSearch/selectors'
import {getSearchIdFromConditionalSource} from 'modules/searchApi/selectors'

import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'

import {getVclidCookie} from '../../modules/cookies/selectors'

declare global {
  interface Window {
    hotelPlannerClientQueue?: (() => void)[]
    hotelPlannerClient: {
      initDynamicPhoneNumberModule: (
        initParams: Record<string, unknown>,
        options: Record<string, unknown>
      ) => void
    }
  }
}

const SCRIPT_SRC = '//cdn.hotelplanner.com/js/partners/client-1.0.0.min.js'
const SCRIPT_TIMEOUT_MS = 10000
const DATE_FORMAT = 'MM/dd/yyyy'

const formatDate = (date: string) => {
  if (!date) return ''
  return format(parseISO(date), DATE_FORMAT)
}

export const INIT_PARAMS = {
  lazyLoad: false,
  formatted: true,
  host: 'vio.lodging.support',
  defaultPhoneNumber: '18885149862'
}

interface EventPayloadArgs {
  payload?: Record<string, unknown> | null
  displayedPhoneNumber?: string | null
  component?: string
}

/** Keeps every CallToBook event on the same payload shape. */
export const getEventPayload = ({
  payload,
  displayedPhoneNumber,
  component
}: EventPayloadArgs) => ({
  ...payload,
  initialParams: INIT_PARAMS,
  displayedPhoneNumber,
  ...(component && {component})
})

const ELIGIBLE_COUNTRIES = ['US', 'CA', 'AU', 'NZ', 'GB'] as const
const ELIGIBLE_LANGUAGES = ['en', 'en-GB'] as const

export type Status = 'loading' | 'ready' | 'error' | 'not-available'

interface Props {
  hotelId?: string
  destination: string
  skip?: boolean
}

export const useCallToBook = ({hotelId, destination, skip = false}: Props) => {
  const [link, setLink] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  // E.164, to match the `Called` number in HotelPlanner's call report.
  const [displayedPhoneNumber, setDisplayedPhoneNumber] = useState('')
  const [status, setStatus] = useState<Status>('loading')

  const {checkIn, checkOut, rooms} = useSelector(getSearchParameters)

  const {numberOfAdults, numberOfChildren, numberOfRooms} =
    roomsToConfigurationObject(rooms)
  // sapiSearch.searchId is only set by the SRP list search, so on AP it's
  // undefined. This falls back to the offers searchId.
  const searchId = useSelector(getSearchIdFromConditionalSource)
  const anonymousId = useSelector(getAnonymousId)
  const pageName = useSelector(getAnalyticsPageName)
  const vclid = useSelector(getVclidCookie)
  const {isMobile} = useDeviceLayout()
  const dispatch = useDispatch()
  const deviceType = isMobile ? 'mobile' : 'desktop'
  const userCountryCode = useSelector(getUserCountryCode)
  const languageCode = useSelector(getLanguageCode)

  const isEligibleMarket =
    ELIGIBLE_LANGUAGES.includes(
      languageCode as (typeof ELIGIBLE_LANGUAGES)[number]
    ) &&
    ELIGIBLE_COUNTRIES.includes(
      userCountryCode as (typeof ELIGIBLE_COUNTRIES)[number]
    )

  const formattedCheckIn = formatDate(checkIn)
  const formattedCheckOut = formatDate(checkOut)

  const shouldStartScript = !skip && isEligibleMarket && checkIn && checkOut

  const payload = useMemo(() => {
    if (!shouldStartScript) {
      return null
    }

    return {
      ...(hotelId && {
        hotelId,
        hotelIdTypeId: '2'
      }),
      destination,
      checkIn: formattedCheckIn,
      checkOut: formattedCheckOut,
      custom1: anonymousId,
      custom2: searchId,
      custom3: pageName,
      custom4: deviceType,
      custom5: vclid,
      rooms: numberOfRooms,
      children: numberOfChildren,
      adults: numberOfAdults
    }
  }, [
    anonymousId,
    shouldStartScript,
    destination,
    deviceType,
    formattedCheckIn,
    formattedCheckOut,
    hotelId,
    numberOfAdults,
    numberOfChildren,
    numberOfRooms,
    pageName,
    searchId,
    vclid
  ])

  // Initialize hotel planner client
  useEffect(() => {
    if (status !== 'ready' || payload === null) return

    window.hotelPlannerClientQueue = window.hotelPlannerClientQueue || []
    window.hotelPlannerClientQueue.push(() => {
      window.hotelPlannerClient.initDynamicPhoneNumberModule(
        /**
         * Warning:
         * Don't pass the variable to this function it seems like this function manipulates the object
         * that was being passed and makes re-renders
         */
        {...INIT_PARAMS},
        {...payload}
      )
    })
  }, [status, payload])

  // Waits for the number, not just script load: without it there is nothing to
  // join the call back to. `payload` keeps changing as the search context loads,
  // so the ref holds this to one event per number shown.
  const trackedPhoneNumber = useRef('')

  useEffect(() => {
    if (status !== 'ready' || !displayedPhoneNumber) return
    if (trackedPhoneNumber.current === displayedPhoneNumber) return

    trackedPhoneNumber.current = displayedPhoneNumber

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.CallToBook,
        action: Action.Loaded,
        page: pageName,
        payload: getEventPayload({payload, displayedPhoneNumber})
      })
    )
  }, [status, displayedPhoneNumber, payload, pageName, dispatch])

  useEffect(() => {
    if (skip) {
      setStatus('not-available')
    }
  }, [skip])

  useEffect(() => {
    if (skip || !shouldStartScript) return

    // Create an element
    const container = document.createElement('span')
    container.style.position = 'absolute'
    container.style.left = '-9999px'
    container.style.visibility = 'hidden'
    // Without this class name the hotelPlanner script can't pickup the element
    container.classList.add('hpPhoneNumber')
    document.body.appendChild(container)

    let observer: MutationObserver | null = null
    let timeout: ReturnType<typeof setTimeout> | null = null

    observer = new MutationObserver(() => {
      // This ensures that we always have consistent formatting across all the places in the app
      const phoneNumber = parsePhoneNumberFromString(
        container.textContent || ''
      )

      if (!phoneNumber) return

      const formattedPhoneNumber = phoneNumber.formatInternational()
      if (formattedPhoneNumber) {
        clearTimeout(timeout)
        setPhoneNumber(formattedPhoneNumber)
        setDisplayedPhoneNumber(phoneNumber.number)
        setLink(phoneNumber.getURI())
      }
    })

    observer.observe(container, {
      childList: true,
      characterData: true,
      subtree: true
    })

    // Create & load script
    timeout = setTimeout(() => {
      setStatus('not-available')
    }, SCRIPT_TIMEOUT_MS)

    const onLoad = () => {
      clearTimeout(timeout)
      setStatus('ready')
    }
    const onError = () => {
      clearTimeout(timeout)
      setStatus('error')
    }

    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true

    script.addEventListener('load', onLoad)
    script.addEventListener('error', onError)
    document.body.appendChild(script)

    return () => {
      // We can't disconnect the observer till we cleanup the DOM
      // because the props might change and we might get a new number
      observer?.disconnect()
      if (timeout) clearTimeout(timeout)
      script.removeEventListener('load', onLoad)
      script.removeEventListener('error', onError)
      script.remove()
      container.remove()
    }
  }, [skip, shouldStartScript])

  return {
    href: link,
    phoneNumber,
    displayedPhoneNumber,
    status,
    payload
  }
}
